import { numberToHex, type Chain } from 'viem'

/*
 * Wallet plumbing for the sign-in dialog, kept free of React so it is unit tested with fake EIP-1193
 * providers (test/wallet-dialog.test.tsx): error classification, the switch-or-add network dance, which
 * wallets to offer (EIP-6963 announcements via wagmi, the legacy `window.ethereum`, WalletConnect), and
 * install or deep links when nothing is installed.
 */

export type WalletErrorKind = 'rejected' | 'pending' | 'locked' | 'unsupported-chain' | 'no-wallet' | 'unknown'

interface ErrLike {
  code?: number
  name?: string
  message?: string
  shortMessage?: string
  cause?: ErrLike
}

function chain(err: unknown): ErrLike[] {
  const out: ErrLike[] = []
  let e = err as ErrLike | undefined
  for (let i = 0; e && typeof e === 'object' && i < 5; i++) {
    out.push(e)
    e = e.cause
  }
  return out
}

export function classifyWalletError(err: unknown): WalletErrorKind {
  const all = chain(err)
  const text = all.map((e) => `${e.name ?? ''} ${e.shortMessage ?? ''} ${e.message ?? ''}`).join(' ')
  if (all.some((e) => e.code === 4001 || e.name === 'UserRejectedRequestError') || /user (rejected|denied|cancel)|rejected the request|request rejected|denied/i.test(text)) return 'rejected'
  if (all.some((e) => e.code === -32002 || e.name === 'ResourceUnavailableRpcError') || /already pending|already processing/i.test(text)) return 'pending'
  if (all.some((e) => e.code === 4902 || (e.name === 'SwitchChainError' && /unrecognized|not been added|unknown chain/i.test(e.message ?? ''))) || /unrecognized chain|chain .* not (been )?added|unknown chain/i.test(text)) return 'unsupported-chain'
  if (all.some((e) => e.name === 'ProviderNotFoundError' || e.name === 'ConnectorNotFoundError') || /provider not found|no ethereum provider/i.test(text)) return 'no-wallet'
  if (/locked|no account|returned no account|unlock/i.test(text)) return 'locked'
  return 'unknown'
}

export function walletErrorCopy(kind: WalletErrorKind, err?: unknown): string {
  switch (kind) {
    case 'rejected':
      return 'You declined in your wallet. Nothing was signed or sent.'
    case 'pending':
      return 'Your wallet already has a request open. Open the wallet, finish or close it, then try again.'
    case 'locked':
      return 'Your wallet is locked or shared no account. Unlock it, then try again.'
    case 'unsupported-chain':
      return 'Your wallet could not add the network. Add it in your wallet settings, then try again.'
    case 'no-wallet':
      return 'No wallet found in this browser.'
    default: {
      const e = err as ErrLike | undefined
      return e?.shortMessage ?? e?.message ?? 'Something went wrong in the wallet.'
    }
  }
}

// Network --------------------------------------------------------------------------------------------

export interface Eip1193 {
  request(args: { method: string; params?: unknown }): Promise<unknown>
}

/** EIP-3085 `wallet_addEthereumChain` parameters for a viem chain (Robinhood Chain or anvil from @algoboo/chain). */
export function addChainParameters(c: Chain) {
  return {
    chainId: numberToHex(c.id),
    chainName: c.name,
    nativeCurrency: c.nativeCurrency,
    rpcUrls: [...c.rpcUrls.default.http],
    ...(c.blockExplorers?.default ? { blockExplorerUrls: [c.blockExplorers.default.url] } : {}),
  }
}

/**
 * Move the wallet to `target`: `wallet_switchEthereumChain`, and when the wallet does not know the chain (4902),
 * `wallet_addEthereumChain` with the chain's parameters, then switch again (some wallets switch on add).
 * Returns what happened; throws the wallet's error otherwise (rejections included).
 */
export async function ensureChain(provider: Eip1193, target: Chain, current?: number): Promise<'already' | 'switched' | 'added'> {
  if (current === target.id) return 'already'
  const hex = numberToHex(target.id)
  try {
    await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] })
    return 'switched'
  } catch (err) {
    if (classifyWalletError(err) !== 'unsupported-chain') throw err
  }
  await provider.request({ method: 'wallet_addEthereumChain', params: [addChainParameters(target)] })
  const now = Number(await provider.request({ method: 'eth_chainId' }).catch(() => '0x0'))
  if (now !== target.id) await provider.request({ method: 'wallet_switchEthereumChain', params: [{ chainId: hex }] })
  return 'added'
}

// Which wallets to offer ---------------------------------------------------------------------------------

export interface ConnectorLike {
  id: string
  uid: string
  name: string
  type: string
  icon?: string | undefined
}

export interface WalletOption<C extends ConnectorLike = ConnectorLike> {
  connector: C
  label: string
  hint: string
  icon?: string | undefined
  kind: 'announced' | 'injected' | 'walletconnect' | 'other'
}

/** rdns → a friendlier hint for wallets we know announce themselves (EIP-6963). */
const KNOWN_RDNS: Record<string, string> = {
  'io.metamask': 'MetaMask extension',
  'io.metamask.flask': 'MetaMask Flask',
  'io.rabby': 'Rabby extension',
  'com.coinbase.wallet': 'Coinbase Wallet',
  'app.phantom': 'Phantom',
  'com.brave.wallet': 'Brave built-in wallet',
  'com.robinhood.wallet': 'Robinhood Wallet',
}

export function walletOptions<C extends ConnectorLike>(connectors: readonly C[], opts: { hasInjected: boolean }): WalletOption<C>[] {
  const announced = connectors.filter((c) => c.type === 'injected' && c.id !== 'injected')
  const out: WalletOption<C>[] = []
  for (const c of announced) out.push({ connector: c, label: c.name, hint: KNOWN_RDNS[c.id] ?? 'Detected in this browser', icon: c.icon, kind: 'announced' })
  for (const c of connectors) {
    if (c.id === 'injected') {
      // the legacy window.ethereum: only when something is there and nothing announced itself
      if (opts.hasInjected && announced.length === 0) out.push({ connector: c, label: 'Browser wallet', hint: 'The wallet extension in this browser', icon: c.icon, kind: 'injected' })
    } else if (c.id === 'walletConnect') out.push({ connector: c, label: 'WalletConnect', hint: 'Scan a QR code with a phone wallet, or open one', icon: c.icon, kind: 'walletconnect' })
    else if (c.type !== 'injected') out.push({ connector: c, label: c.name, hint: 'Connected wallet', icon: c.icon, kind: 'other' })
  }
  return out
}

export const INSTALL_LINKS = [
  { name: 'MetaMask', href: 'https://metamask.io/download/' },
  { name: 'Rabby', href: 'https://rabby.io/' },
  { name: 'Coinbase Wallet', href: 'https://www.coinbase.com/wallet/downloads' },
  { name: 'Phantom', href: 'https://phantom.com/download' },
  { name: 'Brave', href: 'https://brave.com/wallet/' },
] as const

/** Open this page inside a phone wallet's browser (where its injected provider exists). */
export function mobileDeepLinks(href: string): { name: string; href: string }[] {
  const u = new URL(href)
  const noScheme = `${u.host}${u.pathname}${u.search}`
  return [
    { name: 'MetaMask', href: `https://metamask.app.link/dapp/${noScheme}` },
    { name: 'Coinbase Wallet', href: `https://go.cb-w.com/dapp?cb_url=${encodeURIComponent(href)}` },
    { name: 'Phantom', href: `https://phantom.app/ul/browse/${encodeURIComponent(href)}?ref=${encodeURIComponent(u.origin)}` },
  ]
}

export function hasInjectedProvider(): boolean {
  return typeof window !== 'undefined' && !!(window as unknown as { ethereum?: unknown }).ethereum
}

export function isMobileDevice(): boolean {
  if (typeof navigator === 'undefined') return false
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) || (window.matchMedia?.('(pointer: coarse)').matches === true && window.innerWidth < 768)
}
